import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getHeaderFooter, HeaderFooter } from './getHeaderFooter';
import { AEM_RESOURCE_BASE_URL } from '@global/App/constants/constants';
import { triggerAEMHeaderCartIcon } from '@global/hooks/useAEMResource/utils';
import {
  LoadExternalScript,
  LoadExternalStyle,
  LoadInlineScript,
} from '@global/utils/htmlUtils';
type UseAEMResourceState = Pick<HeaderFooter, 'header' | 'footer'>;
type UseAEMResource = UseAEMResourceState & {
  stylesLoaded: boolean;
};

const initialState: UseAEMResource = {
  header: '',
  footer: '',
  stylesLoaded: false,
};

const AEMResourceContext = createContext(initialState);

export const AEMResourceProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  let loaded = false;
  const [stylesLoaded, setStylesLoaded] = useState(false);
  const [state, setState] = useState<UseAEMResourceState>({
    header: initialState.header,
    footer: initialState.footer,
  });
  const updateStyles = (styles: NodeListOf<HTMLStyleElement> | []) => {
    let stylesCount = styles.length;
    if (stylesCount === 0) setStylesLoaded(true);

    const onload = () => {
      stylesCount -= 1;
      if (stylesCount <= 0) setStylesLoaded(true);
    };

    styles.forEach((style) => {
      const styleSrc = style.getAttribute('href');
      const isExternalStyle = styleSrc && styleSrc.startsWith('https');

      if (!isExternalStyle)
        LoadExternalStyle(AEM_RESOURCE_BASE_URL + styleSrc, onload);
      if (isExternalStyle) onload();
    });
  };

  const updateScripts = (scripts: NodeListOf<HTMLScriptElement> | []) => {
    scripts.forEach((script) => {
      const scriptSrc = script.getAttribute('src');

      if (!scriptSrc) {
        LoadInlineScript(script.innerHTML);
        return;
      }

      // Check if it's a wiley clientlib script
      const clientlibsIndex = scriptSrc.indexOf('/etc.clientlibs/');

      if (clientlibsIndex === -1) {
        LoadExternalScript(scriptSrc);
        return;
      }

      const externalScriptUrl =
        AEM_RESOURCE_BASE_URL + scriptSrc.slice(clientlibsIndex);
      LoadExternalScript(externalScriptUrl);
    });
  };

  // Function to extract and load scripts from header/footer HTML content
  const extractAndLoadScriptsFromHTML = (htmlContent: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const scripts = doc.querySelectorAll('script');
    scripts.forEach((script) => {
      const scriptSrc = script.getAttribute('src');
      if (scriptSrc) {
        LoadExternalScript(scriptSrc);
      }
    });
  };

  const loadCSS = (styles: string[]) => {
    if (!styles || styles.length === 0) {
      setStylesLoaded(true);
      return;
    }
    let stylesCount = styles.length || 0;
    const onload = () => {
      stylesCount -= 1;
      if (stylesCount <= 0) setStylesLoaded(true);
    };

    styles.forEach((styleSrc) => {
      LoadExternalStyle(styleSrc, onload);
    });
  };

  const getHtml = useCallback(async () => {
    const response = await getHeaderFooter();
    if (response.source) {
      // spectrabase returns header/footer HTML and a headerFooterCSS link (string)
      // const cssHref = response.styles as unknown as string;

      loadCSS(response.styles as unknown as string[]);
      // Extract and load scripts from header and footer HTML content
      extractAndLoadScriptsFromHTML(response.header);
      extractAndLoadScriptsFromHTML(response.footer);
      setState({
        header: response.header,
        footer: response.footer,
      });
    } else {
      // default behaviour for legacy combined header/footer
      updateStyles(response.styles as any);
      updateScripts(response.scripts as any);
      setState({
        header: response.header,
        footer: response.footer,
      });
    }
  }, []);

  useEffect(() => {
    if (!loaded) {
      getHtml();
      loaded = true;
    }
  }, [getHtml]);

  useEffect(() => {
    triggerAEMHeaderCartIcon();
  }, [stylesLoaded]);

  const payload = useMemo(
    () => ({ ...state, stylesLoaded }),
    [state, stylesLoaded]
  );

  return (
    <AEMResourceContext.Provider value={payload}>
      {children}
    </AEMResourceContext.Provider>
  );
};

export const useAEMResource = (): UseAEMResource => {
  const context = useContext(AEMResourceContext);

  if (!context) {
    throw new Error('useAEMResource must be used within a AEMResourceProvider');
  }
  return context;
};
